import {
  Controller,
  Control,
  Path,
  useFormState,
  FieldValues,
  ControllerRenderProps,
} from 'react-hook-form';
import get from 'lodash.get';
import {
  InputLabel,
  Box,
  TextField as MuiTextField,
  StandardTextFieldProps as MuiTextFieldProps,
} from '@mui/material';

export interface TextFieldProps<T extends FieldValues> extends MuiTextFieldProps {
  control: Control<T, object>;
  name: Path<T>;
  outsideError?: string;
  label?: React.ReactNode;
  placeholder?: string;
  type?: string;
  disabled?: boolean;
  multiline?: boolean;
  rows?: number;
  required?: boolean;
  returnNumber?: boolean;
}

export const TextField = <T extends object>({
  name,
  control,
  label,
  placeholder,
  type,
  disabled,
  outsideError,
  sx,
  required,
  returnNumber,
  ...props
}: TextFieldProps<T>) => {
  const { errors } = useFormState({ control });
  const error = (get(errors, name) as any)?.message || outsideError;

  const onChange =
    (field: ControllerRenderProps<T, Path<T>>) =>
    (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) =>
      field.onChange(returnNumber ? parseInt(e?.target?.value, 10) : e?.target?.value);

  const _renderInput = ({ field }: { field: ControllerRenderProps<T, Path<T>> }) => (
    <Box width="100%" sx={sx}>
      {label && (
        <InputLabel htmlFor={name}>
          {label}
          {required && <span>&nbsp;*</span>}
        </InputLabel>
      )}
      <MuiTextField
        {...field}
        disabled={disabled}
        type={type}
        id={name}
        placeholder={placeholder}
        error={!!error}
        variant="outlined"
        helperText={error}
        onChange={onChange(field)}
        {...props}
      />
    </Box>
  );

  return <Controller name={name} control={control} render={_renderInput} />;
};
